/**
 * useAddDeviceFlow
 *
 * Owns the "add a device" flow extracted from DevicesPage: form field state,
 * connection test / discovery, and the single / Focustronic-bulk / Kamoer-bulk
 * submit paths. Behavior is a faithful move of the original DevicesPage logic.
 *
 * The hook does NOT own the duplicate "update existing" modal. When a device of
 * the chosen type already exists (only possible when `existingDevices` is
 * provided), it calls `onDuplicate(existing, payload)` and the consumer decides
 * what to do. On success it invalidates the devices + tank-parameters caches so
 * the list and nav capability flags refresh, then calls `onSuccess`.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { tankParametersQueryKey } from '../../api/parameters';
import {
  DeviceConfig,
  DeviceType,
  KamoerDiscoverResponse,
  KamoerDiscoveredDevice,
  KamoerBulkCreateResponse,
} from '../../types/devices';
import { CreateDeviceData, DEVICE_TYPE_OPTIONS } from './constants';
import { buildCredentials, validateForm } from './credentialFields';

type TestResult = { success: boolean; message: string } | null;

interface AvailableDevice {
  type: string;
  id: number | string;
  serial_number?: string;
  friendly_name: string;
  is_active: boolean;
}

/** Payload handed to `onDuplicate` so the consumer can perform the update. */
export interface AddDeviceDuplicatePayload {
  deviceType: DeviceType;
  credentials: ReturnType<typeof buildCredentials>;
  /** false = single add path (one existing match), true = bulk add path. */
  isBulk: boolean;
}

export interface UseAddDeviceFlowOptions {
  tankId: string;
  /** Currently configured devices for the tank; used to detect duplicates. */
  existingDevices?: DeviceConfig[];
  /** Called after a fully successful add (caller closes the modal / advances). */
  onSuccess: () => void | Promise<void>;
  /** Called instead of POSTing when a device of the type already exists. */
  onDuplicate?: (existing: DeviceConfig[], payload: AddDeviceDuplicatePayload) => void;
  /**
   * Pre-selects the device type on mount (e.g. the onboarding wizard's connect
   * step preselects the first brand the user picked). Mirrors what
   * handleDeviceTypeChange does: the display name is auto-filled with the type's
   * label so the initial state matches a manual selection of the same type.
   */
  initialDeviceType?: DeviceType | null;
}

/** Label for a device type, or '' when unknown / null (matches manual select). */
function labelForDeviceType(type: DeviceType | null | undefined): string {
  if (!type) return '';
  return DEVICE_TYPE_OPTIONS.find((opt) => opt.value === type)?.label || '';
}

export function useAddDeviceFlow({
  tankId,
  existingDevices,
  onSuccess,
  onDuplicate,
  initialDeviceType = null,
}: UseAddDeviceFlowOptions) {
  const queryClient = useQueryClient();

  const devices = existingDevices ?? [];

  // Form state. When initialDeviceType is provided we seed both the type and the
  // display name so the starting state is identical to picking that type by hand.
  const [deviceType, setDeviceTypeState] = useState<DeviceType | null>(initialDeviceType);
  const [displayName, setDisplayName] = useState(() => labelForDeviceType(initialDeviceType));
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [enableWaterTests, setEnableWaterTests] = useState(true);
  const [enableMaintenance, setEnableMaintenance] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Test before save state
  const [isTesting, setIsTesting] = useState(false);
  const [testBeforeSaveResult, setTestBeforeSaveResult] = useState<TestResult>(null);

  // Focustronic device selection state
  const [availableDevices, setAvailableDevices] = useState<AvailableDevice[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<number>>(new Set());

  // AquaWiz device selection (single select)
  const [selectedAquaWizDevice, setSelectedAquaWizDevice] = useState<string | null>(null);

  // Kamoer device discovery state
  const [kamoerDevices, setKamoerDevices] = useState<KamoerDiscoveredDevice[]>([]);
  const [selectedKamoerDeviceIds, setSelectedKamoerDeviceIds] = useState<Set<string>>(new Set());
  const [kamoerProviderAccountId, setKamoerProviderAccountId] = useState<string | null>(null);

  const credentialValues = {
    username,
    email,
    password,
    enableWaterTests,
    enableMaintenance,
    selectedAquaWizDevice,
  };

  const validationValues = { displayName, username, email, password };

  /**
   * Refetch the device list and invalidate tank-parameters so nav capability
   * flags (e.g. Dosing) update immediately after a device mutation.
   */
  const refreshCaches = async () => {
    await queryClient.invalidateQueries({ queryKey: ['devices', tankId] });
    queryClient.invalidateQueries({ queryKey: tankParametersQueryKey(tankId) });
  };

  // Select handler: also auto-populates the display name with the type label.
  const handleDeviceTypeChange = (value: string | null) => {
    const newType = value as DeviceType;
    setDeviceTypeState(newType);
    if (newType) {
      const label = DEVICE_TYPE_OPTIONS.find((opt) => opt.value === newType)?.label || '';
      setDisplayName(label);
    }
  };

  const resetForm = () => {
    setDeviceTypeState(null);
    setDisplayName('');
    setUsername('');
    setEmail('');
    setPassword('');
    setEnableWaterTests(true);
    setEnableMaintenance(true);
    setFormError(null);
    setTestBeforeSaveResult(null);
    setAvailableDevices([]);
    setSelectedDeviceIds(new Set());
    setSelectedAquaWizDevice(null);
    setKamoerDevices([]);
    setSelectedKamoerDeviceIds(new Set());
    setKamoerProviderAccountId(null);
  };

  // Check if a device of this type already exists
  const checkForExistingDevice = (checkDeviceType: string): DeviceConfig | undefined => {
    if (!devices || !checkDeviceType) return undefined;
    return devices.find((d) => d.device_type === checkDeviceType);
  };

  // Check if any of the given device types already exist (bulk add)
  const checkForExistingDevices = (deviceTypes: string[]): DeviceConfig[] => {
    if (!devices || deviceTypes.length === 0) return [];
    return devices.filter((d) => deviceTypes.includes(d.device_type));
  };

  // Test connection before saving (Add Device modal)
  const handleTestBeforeSave = async () => {
    if (!tankId || !deviceType) return;
    const validationError = validateForm(deviceType, validationValues);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsTesting(true);
    setTestBeforeSaveResult(null);

    try {
      // Kamoer uses the provider account discover endpoint
      if (deviceType === 'kamoer_kh') {
        const response = await api.post<KamoerDiscoverResponse>(
          `/api/tanks/${tankId}/provider-accounts/kamoer/discover`,
          { email, password, display_name: 'Kamoer' },
        );

        setTestBeforeSaveResult({
          success: response.success,
          message: `Connected successfully. Found ${response.devices.length} device(s).`,
        });
        setKamoerDevices(response.devices);
        setKamoerProviderAccountId(response.provider_account.id);
      } else {
        // All other device types use the standard test endpoint
        const response = await api.post<{
          success: boolean;
          message: string;
          devices?: AvailableDevice[];
        }>(`/api/tanks/${tankId}/devices/test`, {
          device_type: deviceType,
          credentials: buildCredentials(deviceType, credentialValues),
        });

        setTestBeforeSaveResult(response);

        // Store available devices for Focustronic and AquaWiz
        if ((deviceType === 'focustronic' || deviceType === 'aquawiz') && response.devices) {
          setAvailableDevices(response.devices);
          // Auto-select if only one AquaWiz device
          if (deviceType === 'aquawiz' && response.devices.length === 1 && response.devices[0]) {
            setSelectedAquaWizDevice(String(response.devices[0].id));
          }
        }
      }
    } catch (err) {
      setTestBeforeSaveResult({
        success: false,
        message: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Submit add form (single device)
  const handleSubmitAdd = async () => {
    if (!tankId || !deviceType) return;
    const validationError = validateForm(deviceType, validationValues);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    // Check for existing device of this type
    const existing = checkForExistingDevice(deviceType);
    if (existing) {
      setFormError(null); // Clear any stale errors before showing modal
      onDuplicate?.([existing], {
        deviceType,
        credentials: buildCredentials(deviceType, credentialValues),
        isBulk: false,
      });
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const data: CreateDeviceData = {
        device_type: deviceType,
        display_name: displayName.trim(),
        credentials: buildCredentials(deviceType, credentialValues),
      };

      await api.post(`/api/tanks/${tankId}/devices`, data);
      await refreshCaches();
      resetForm();
      await onSuccess();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add device');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit bulk add for Focustronic devices
  const handleBulkAdd = async () => {
    if (!tankId || deviceType !== 'focustronic') return;

    // Capture state immediately to avoid race conditions
    const currentSelections = new Set(selectedDeviceIds);

    if (currentSelections.size === 0) {
      setFormError('Please select at least one device to add');
      return;
    }

    // Build devices array from selected devices using captured state
    // Note: Focustronic uses numeric IDs, so cast is safe here
    const selectedDevices = availableDevices
      .filter((d) => currentSelections.has(d.id as number))
      .map((d) => ({
        type: d.type,
        id: d.id,
        serial_number: d.serial_number,
        friendly_name: `${d.type.charAt(0).toUpperCase() + d.type.slice(1)} - ${d.serial_number}`,
      }));

    // Check for existing devices of selected types (e.g., mastertronic, dosetronic)
    const selectedTypes = selectedDevices.map((d) => d.type);
    const existingMatches = checkForExistingDevices(selectedTypes);
    if (existingMatches.length > 0) {
      setFormError(null); // Clear any stale errors before showing modal
      onDuplicate?.(existingMatches, {
        deviceType,
        credentials: buildCredentials(deviceType, credentialValues),
        isBulk: true,
      });
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const data = {
        credentials: buildCredentials(deviceType, credentialValues),
        devices: selectedDevices,
      };

      const response = await api.post<{
        created: Array<Record<string, unknown>>;
        errors: Array<{ device_type: string; error: string }>;
        total_created: number;
        total_errors: number;
      }>(`/api/tanks/${tankId}/devices/bulk`, data);

      // Show results
      if (response.total_created > 0) {
        await refreshCaches();
        resetForm();
        await onSuccess();
      }

      if (response.total_errors > 0) {
        const errorMsg = response.errors.map((e) => `${e.device_type}: ${e.error}`).join('\n');
        setFormError(`Some devices failed:\n${errorMsg}`);
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add devices');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit bulk add for Kamoer devices via provider account
  const handleKamoerBulkAdd = async () => {
    if (!tankId || deviceType !== 'kamoer_kh') return;

    if (selectedKamoerDeviceIds.size === 0) {
      setFormError('Please select at least one device to add');
      return;
    }

    if (!kamoerProviderAccountId) {
      setFormError('No provider account found. Please run discovery first.');
      return;
    }

    const selectedDevicesArr = kamoerDevices.filter((d) => selectedKamoerDeviceIds.has(d.iotId));

    // Check for existing devices of selected types
    const selectedTypes = selectedDevicesArr.map((d) => d.device_type);
    const existingMatches = checkForExistingDevices(selectedTypes);
    if (existingMatches.length > 0) {
      setFormError(null);
      onDuplicate?.(existingMatches, {
        deviceType,
        credentials: buildCredentials(deviceType, credentialValues),
        isBulk: true,
      });
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const displayNameMap: Record<string, string> = {
        kamoer_kh: 'KH Carer',
        kamoer_stp: 'STP Connect',
        kamoer_p4: 'P4 Pro',
      };

      const response = await api.post<KamoerBulkCreateResponse>(
        `/api/tanks/${tankId}/devices/kamoer/bulk`,
        {
          provider_account_id: kamoerProviderAccountId,
          devices: selectedDevicesArr.map((d) => ({
            device_type: d.device_type,
            iot_id: d.iotId,
            display_name: displayNameMap[d.device_type] || d.productName || d.productModel,
            product_model: d.productModel,
            product_name: d.productName,
            device_name: d.deviceName,
          })),
        },
      );

      if (response.total_created > 0) {
        await refreshCaches();
      }

      if (response.total_errors > 0) {
        const errorMsg = response.errors.map((e) => `${e.device_type}: ${e.error}`).join('\n');
        setFormError(`Some devices failed:\n${errorMsg}`);
        if (response.total_created > 0) {
          // Partial success -- keep modal open but show error
          return;
        }
      } else {
        resetForm();
        await onSuccess();
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add devices');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dispatch to the correct submit path based on device type.
  const handleSubmit = () => {
    if (deviceType === 'focustronic') return handleBulkAdd();
    if (deviceType === 'kamoer_kh') return handleKamoerBulkAdd();
    return handleSubmitAdd();
  };

  return {
    // State
    deviceType,
    displayName,
    username,
    email,
    password,
    enableWaterTests,
    enableMaintenance,
    isSubmitting,
    formError,
    isTesting,
    testBeforeSaveResult,
    availableDevices,
    selectedDeviceIds,
    selectedAquaWizDevice,
    kamoerDevices,
    selectedKamoerDeviceIds,
    kamoerProviderAccountId,
    // Setters
    setDisplayName,
    setUsername,
    setEmail,
    setPassword,
    setEnableWaterTests,
    setEnableMaintenance,
    setFormError,
    setSelectedDeviceIds,
    setSelectedAquaWizDevice,
    setSelectedKamoerDeviceIds,
    // Handlers
    handleDeviceTypeChange,
    handleTestBeforeSave,
    handleSubmitAdd,
    handleBulkAdd,
    handleKamoerBulkAdd,
    handleSubmit,
    resetForm,
  };
}
